import { z } from 'zod';

/**
 * パスワードバリデーション（superRefine）
 *
 * パスワードは任意入力（空の場合はサーバー側で更新しない）。
 * パスワードを入力した場合のみ 8文字以上・英数字混合・確認一致を検証する。
 */
const passwordRefinement = (
  data: { password?: string; passwordConfirmation?: string },
  ctx: z.RefinementCtx
): void => {
  const { password, passwordConfirmation } = data;
  if (!password) return;

  if (password.length < 8) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'パスワードは8文字以上入力してください。',
      path: ['password'],
    });
  } else if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'パスワードは英数字混合にしてください。',
      path: ['password'],
    });
  }

  if (password !== passwordConfirmation) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'パスワードとパスワード（再入力）が一致しません。',
      path: ['passwordConfirmation'],
    });
  }
};

/**
 * 利用者編集フォーム（口座連携版）の Zod スキーマ
 *
 * メールアドレス必須・パスワードは任意（入力した場合のみ検証）。
 * billableFrom は常に必須（利用開始日フィールドは常時表示のため、必須バリデーションも常に適用する）。
 * 引数を取らないため関数ではなく定数として定義する。
 */
export const editUserFormBankAccountSchema = z
  .object({
    fullName: z.string().min(1, '氏名が入力されていません。'),
    email: z
      .string()
      .min(1, 'メールアドレスが入力されていません。')
      .regex(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'メールアドレスの形式が正しくありません。'
      ),
    externalUserId: z.string().optional(),
    password: z.string().optional(),
    passwordConfirmation: z.string().optional(),
    // .refine(v => v !== null) は実行時チェックを追加するだけで billableFrom の型を
    // Date に絞り込まない（Zod の型推論は refine の述語からは narrow されない）ため、
    // .transform で明示的に Date へキャストし、以降（generateRequest 等）で
    // billableFrom が null を考慮した分岐を書かずに済むようにする。
    billableFrom: z
      .date({ invalid_type_error: '利用開始日を選択してください。' })
      .nullable()
      .refine(v => v !== null, { message: '利用開始日を選択してください。' })
      .transform(v => v as Date),
  })
  .superRefine(passwordRefinement);

export type EditUserFormBankAccountFieldValues = z.infer<typeof editUserFormBankAccountSchema>;
